/**
 * Button component with variants inspired by Catalyst UI
 */

import { Button as HeadlessButton } from "@headlessui/react";
import type { ComponentPropsWithoutRef } from "react";

type ButtonVariant = "primary" | "secondary" | "danger" | "success" | "ghost";
type ButtonSize = "sm" | "md";

interface ButtonProps extends ComponentPropsWithoutRef<"button"> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary: "border-none bg-accent-blue text-white hover:bg-accent-blue-hover",
  secondary:
    "border border-border-primary bg-surface-tertiary text-text-primary hover:bg-surface-secondary",
  danger: "border-none bg-status-error text-white hover:bg-status-error-hover",
  success: "border-none bg-accent-green text-white hover:bg-accent-green-hover",
  ghost: "border-none bg-transparent text-accent-blue underline",
};

const sizeClasses: Record<ButtonSize, string> = {
  // min-h-11 enforces the WCAG 2.5.5 / Apple HIG / Material 44px minimum
  // tap-target height. Buttons already taller than 44px are unaffected.
  sm: "min-h-11 px-3 py-1.5 text-sm",
  md: "px-6 py-3 text-sm font-semibold",
};

export function Button({
  variant = "primary",
  size = "md",
  disabled,
  className = "",
  children,
  ...rest
}: ButtonProps) {
  return (
    <HeadlessButton
      disabled={disabled}
      className={`rounded-md transition-colors ${
        disabled
          ? "cursor-not-allowed bg-text-muted text-white"
          : `cursor-pointer ${variantClasses[variant]}`
      } ${sizeClasses[size]} ${className}`}
      {...rest}
    >
      {children}
    </HeadlessButton>
  );
}
